import LockIcon from "@mui/icons-material/Lock";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { FormTextField, GridCardContent } from "components/atoms";
import { FormContainer, FormContent } from "components/molecules";
import {
  FormikContextType,
  FormikHelpers,
  useFormik,
  useFormikContext,
} from "formik";
import { DialogHelper, GenerateHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import { filter, isUndefined, map } from "lodash";
import { IPaginationRequest, IWarehouse } from "model";
import { IRole } from "model/Role";
import {
  ICreateUserRequest,
  IUpdateUserRequest,
  IUser,
} from "model/User/IUser";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { MasterDataActions, UserActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { RoleApiService, WarehouseApiService } from "services";
import { EMAIL_PATTERN } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";
import ChangePasswordModal from "./components/ChangePasswordModal";

enum EStatus {
  ACTIVE = "A",
  IN_ACTIVE = "I",
}

const validationSchema = yup.object().shape({
  firstName: yup.string().required(translations.yupError.required),
  lastName: yup.string().required(translations.yupError.required),
  email: yup.string().required(translations.yupError.required),
});

const UserDetail = () => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const isAdd = location.pathname.includes("new");
  const action = useAppSelector((state) => state.MasterDataReducer.user.action);
  const currentUser = useAppSelector(
    (state) => state.MasterDataReducer.user.current
  );

  const initialValues = isAdd
    ? ({
        firstName: "",
        lastName: "",
        userName: "",
        profilePicture: "",
        email: "",
        password: "",
        confirmPassword: "",
        phoneNumber: "",
        roles: ["Operator"],
        warehouseLists: [],
        isActive: EStatus.ACTIVE,
      } as ICreateUserRequest)
    : ({
        id: currentUser?.id,
        firstName: currentUser?.firstName,
        lastName: currentUser?.lastName,
        email: currentUser?.email,
        phoneNumber: currentUser?.phoneNumber,
        userName: currentUser?.userName,
        roles: currentUser?.roles,
        warehouseLists: currentUser?.warehouses.map((e) => {
          return {
            warehouseId: e.id,
          };
        }),
        isActive: currentUser?.isActive ? EStatus.ACTIVE : EStatus.IN_ACTIVE,
      } as IUpdateUserRequest);
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (
      values: ICreateUserRequest | IUpdateUserRequest,
      { validateForm }: FormikHelpers<ICreateUserRequest | IUpdateUserRequest>
    ) => {
      validateForm().then(() => {
        const password = GenerateHelper.generatePassword();
        const params = values as ICreateUserRequest;
        const createRequest: ICreateUserRequest = {
          userName: params.userName,
          firstName: params.firstName,
          lastName: params.lastName,
          email: params.email + EMAIL_PATTERN,
          phoneNumber: params.phoneNumber,
          password: password,
          confirmPassword: password,
          profilePicture: params.profilePicture,
          warehouseLists: values.warehouseLists,
          roles: values.roles,
          isActive: values.isActive === EStatus.ACTIVE,
        };
        if (isAdd) {
          dispatch(MasterDataActions.createUser.request(createRequest));
        } else {
          const params = values as IUpdateUserRequest;
          const updateRequest: IUpdateUserRequest = {
            id: params.id,
            firstName: params.firstName,
            lastName: params.lastName,
            email: params.email,
            phoneNumber: params.phoneNumber,
            newPassword: "",
            userName: params.userName,
            warehouseLists: params.warehouseLists,
            roles: params.roles,
            isActive: values.isActive === EStatus.ACTIVE,
          };
          dispatch(MasterDataActions.updateUser.request(updateRequest));
        }
      });
    },
  });

  const createOrUpdateUserSuccessfully =
    action.includes(MasterDataActions.createUser.successName) ||
    action.includes(MasterDataActions.updateUser.successName) ||
    action.includes(MasterDataActions.deleteUser.successName);

  const getCardTitle = () => {
    return isAdd
      ? translations.masterData.addUser
      : translations.masterData.updateUser;
  };
  const handleDeleteItem = () => {
    DialogHelper.openConfirmDeleteDialog({
      itemName: currentUser?.userName,
      onDelete: () => _deleteUser(currentUser?.id!),
    });
  };

  const _getUserById = () => {
    dispatch(MasterDataActions.getUserById.request({ id: params.id }));
  };
  const _clearCurrentUser = () => {
    dispatch(MasterDataActions.clearCurrentUser.request());
  };
  const _deleteUser = (id: string) => {
    dispatch(
      MasterDataActions.deleteUser.request({
        id: id,
      })
    );
  };

  useEffect(() => {
    if (!isAdd) {
      _getUserById();
    }
    return () => {
      _clearCurrentUser();
    };
  }, []);

  useEffect(() => {
    if (createOrUpdateUserSuccessfully) {
      history.go(-1);
    }
  }, [action]);

  return (
    <GridCardContent>
      <Box sx={{ p: 2, width: "100%" }}>
        <FormContainer
          formik={formik}
          loading={action.includes("_REQUEST")}
          isAdd={isAdd}
          onDelete={handleDeleteItem}
        >
          <Typography variant="h3">{I18nTranslator(getCardTitle())}</Typography>
          <Form formik={formik} />
        </FormContainer>
      </Box>
    </GridCardContent>
  );
};

const Form = (props: { formik: FormikContextType<any> }) => {
  const { formik } = props;
  const location = useLocation();
  const isAdd = location.pathname.includes("new");
  return (
    <Box sx={{ py: 2 }}>
      <FormGeneralInformation />
      <FormRoleSelection formik={formik} />
      <FormWarehouseSelection formik={formik} />
      {!isAdd && <FormUpdateSecurity />}
    </Box>
  );
};

const FormGeneralInformation = () => {
  const { setFieldValue } = useFormikContext();
  const location = useLocation();
  const isAdd = location.pathname.includes("new");
  return (
    <FormContent
      title={I18nTranslator(translations.masterData.generaInformation)}
    >
      <Grid xs={6} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.firsName)}
          placeholder={I18nTranslator(translations.placeholder.firsName)}
          id="firstName"
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.lastName)}
          placeholder={I18nTranslator(translations.placeholder.lastName)}
          id="lastName"
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          required
          label={I18nTranslator(translations.masterData.email)}
          placeholder={I18nTranslator(translations.placeholder.email)}
          id="email"
          onChange={(e: any) => {
            const value: string = e.target.value;
            setFieldValue("email", value);
            setFieldValue("userName", value.substring(0, value.indexOf("@")));
          }}
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          label={I18nTranslator(translations.masterData.phoneNumber)}
          placeholder={I18nTranslator(translations.placeholder.phoneNumber)}
          id="phoneNumber"
          inputProps={{
            inputMode: "numeric",
            pattern: "/^-?d+(?:.d+)?$/g",
          }}
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          disabled
          label={I18nTranslator(translations.masterData.userName)}
          fullWidth
          placeholder={I18nTranslator(translations.placeholder.userName)}
          id="userName"
        />
      </Grid>

      <Grid xs={6} item>
        <FormTextField
          label={I18nTranslator(translations.masterData.status)}
          select
          id="isActive"
          disabled={isAdd}
        >
          <MenuItem value={EStatus.ACTIVE}>
            {I18nTranslator(translations.masterData.active)}
          </MenuItem>
          <MenuItem value={EStatus.IN_ACTIVE}>
            {I18nTranslator(translations.masterData.inactive)}
          </MenuItem>
        </FormTextField>
      </Grid>
    </FormContent>
  );
};

const FormUpdateSecurity = () => {
  const dispatch = useAppDispatch();
  const action = useAppSelector((state) => state.UserReducer.action);
  const currentUser = useAppSelector(
    (state) => state.MasterDataReducer.user.current
  );
  const _updateUser = (request: IUpdateUserRequest) => {
    dispatch(UserActions.changePasswordUser.request(request));
  };

  const handleChangePassword = (password: string) => {
    const request: IUpdateUserRequest = {
      id: currentUser?.id!,
      firstName: currentUser?.firstName!,
      lastName: currentUser?.lastName!,
      userName: currentUser?.userName!,
      email: currentUser?.email!,
      newPassword: password,
      phoneNumber: currentUser?.phoneNumber!,
      warehouseLists: currentUser?.warehouses.map((e) => {
        return {
          warehouseId: e.id,
        };
      })!,
      roles: currentUser?.roles!,
      isActive: currentUser?.isActive!,
    };
    setOpenChangePasswordModal(false);
    _updateUser(request);
  };
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  return (
    <FormContent
      title={I18nTranslator(translations.masterData.updateUserSecurity)}
    >
      <Stack sx={{ marginTop: "10px" }} direction={"row"}>
        <LoadingButton
          loading={action.includes(UserActions.changePasswordUser.requestName)}
          startIcon={<LockIcon />}
          variant="contained"
          onClick={() => setOpenChangePasswordModal(true)}
        >
          {I18nTranslator(translations.button.updatePassword)}
        </LoadingButton>
      </Stack>
      <ChangePasswordModal
        open={openChangePasswordModal}
        onConfirm={handleChangePassword}
        onClose={() => setOpenChangePasswordModal(false)}
      />
    </FormContent>
  );
};

const FormRoleSelection = (props: { formik: FormikContextType<IUser> }) => {
  const {
    formik: { values, setFieldValue },
  } = props;
  const [request] = useState({ pageNumber: 1, pageSize: 1000 });

  const { loading, result } = usePaginationRequest<IPaginationRequest, IRole[]>(
    request,
    RoleApiService.getAllRoles
  );

  const filterRoles = useMemo(() => {
    if (isUndefined(result.data)) {
      return [];
    }
    return map(result.data, (role) => {
      return {
        isActive: values?.roles?.includes(role.name),
        data: role,
      };
    });
  }, [values?.roles, result.data]);

  const handleUpdateRole = (name: string) => {
    const arr = [...values.roles];
    const isExist = values.roles.includes(name);
    const newRoles = isExist
      ? filter(arr, (role) => role !== name)
      : arr.concat([name]);
    setFieldValue("roles", newRoles);
  };

  return (
    <FormContent title={I18nTranslator(translations.masterData.role)}>
      <Grid container item columnSpacing={1} rowSpacing={1}>
        {loading ? (
          <Grid xs={12} item>
            <Stack direction={"row"} spacing={2}>
              <CircularProgress size={20} />
              <Typography>Đang lấy danh sách vài trò</Typography>
            </Stack>
          </Grid>
        ) : (
          filterRoles.map((role) => {
            return (
              <Grid xs={2} item>
                <Button
                  fullWidth
                  sx={{ p: 2, borderRadius: "10px" }}
                  variant={role.isActive ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleUpdateRole(role.data.name)}
                >
                  {role.data.name}
                </Button>
              </Grid>
            );
          })
        )}
      </Grid>
    </FormContent>
  );
};

const FormWarehouseSelection = (props: {
  formik: FormikContextType<ICreateUserRequest>;
}) => {
  const {
    formik: { values, setFieldValue },
  } = props;
  const [request] = useState({ pageNumber: 1, pageSize: 1000 });
  const { loading, result } = usePaginationRequest<
    IPaginationRequest,
    IWarehouse[]
  >(request, WarehouseApiService.getAllWarehouses);

  const filterWarehouses = useMemo(() => {
    if (isUndefined(result.data)) {
      return [];
    }
    return map(result.data, (warehouse) => {
      return {
        isActive: values?.warehouseLists?.some(
          (e) => e.warehouseId === warehouse.id
        ),
        data: warehouse,
      };
    });
  }, [values?.warehouseLists, result.data]);

  const handleUpdateWarehouse = (warehouse: IWarehouse) => {
    const arr = [...values.warehouseLists];
    const isExist = values.warehouseLists.some(
      (e) => e.warehouseId === warehouse.id
    );
    const newWarehouse = isExist
      ? filter(arr, (e) => e.warehouseId !== warehouse.id)
      : arr.concat([{ warehouseId: warehouse.id }]);
    setFieldValue("warehouseLists", newWarehouse);
  };

  return (
    <FormContent title={I18nTranslator(translations.masterData.warehouse)}>
      <Grid container item columnSpacing={1} rowSpacing={1}>
        {loading ? (
          <Grid xs={12} item>
            <Stack direction={"row"} spacing={2}>
              <CircularProgress size={20} />
            </Stack>
          </Grid>
        ) : (
          filterWarehouses.map((warehouse) => {
            return (
              <Grid xs={2} item>
                <Button
                  fullWidth
                  sx={{ p: 2, borderRadius: "10px" }}
                  variant={warehouse.isActive ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleUpdateWarehouse(warehouse.data)}
                >
                  {warehouse.data.description}
                </Button>
              </Grid>
            );
          })
        )}
      </Grid>
    </FormContent>
  );
};

export default UserDetail;
